import { ButtonHTMLAttributes, useMemo } from 'react'

import { setupChatToggle } from '@livekit/components-core'

import { useLayoutContext } from '@/src/context'
import { mergeProps } from '@/src/mergeProps'

import { useAppDispatch, useAppSelector } from '@/src/store/hooks'
import { selectUnreadMessages } from '@/src/store/slices/chat'
import { setCurrentTab } from '@/src/store/slices/pip'

/** @public */
export interface UseChatToggleProps {
  props: ButtonHTMLAttributes<HTMLButtonElement>
}

/**
 * The `useChatToggle` hook provides state and functions for toggling the chat window.
 * @remarks
 * Depends on the `LayoutContext` to work properly.
 * @see {@link ChatToggle}, {@link Chat}
 * @public
 */

export function useChatToggle({ props }: UseChatToggleProps) {
  const { dispatch, state } = useLayoutContext().widget
  const dispatchPiP = useAppDispatch()
  const unreadMessageCount = useAppSelector(selectUnreadMessages)
  const { className } = useMemo(() => setupChatToggle(), [])

  const mergedProps = useMemo(
    () =>
      mergeProps(props, {
        className: `${className} ${props?.className || ''}`,
        onClick: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
          if (props.onClick) {
            props.onClick(e)
          }

          if (state?.showChat) {
            dispatchPiP(setCurrentTab('home'))
          } else {
            dispatchPiP(setCurrentTab('chat'))
          }

          if (dispatch) {
            dispatch({ msg: 'toggle_chat' })
          }
        },
        'aria-pressed': state?.showChat ? 'true' : 'false',
        'data-lk-unread-msgs': unreadMessageCount
          ? unreadMessageCount < 10
            ? unreadMessageCount.toFixed(0)
            : '9+'
          : '0',
      }),
    [props, className, state?.showChat, unreadMessageCount],
  )

  return { mergedProps, unreadMessages: unreadMessageCount }
}
